/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import navigationBarStyle from "./navigationBarStyle";
import Toolbar from "@mui/material/Toolbar";
import { withScroll } from "../../../services/withScroll";
import { AppBar, Box, Button, Link as MuiLink, Menu, MenuItem, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import Logo from "../Logo/Logo";
import { KeyboardArrowDownRounded, KeyboardArrowUpRounded } from "@mui/icons-material";
import LoginIcon from "../../LoginIcon/LoginIcon";
import LoggedInMenu from "../../LoginIcon/LoggedInMenu";
import _map from "lodash/map";
import { navigationLinks } from "../../../services/layoutService";
import { useCustomer } from "../../../provider/Customer/CustomerProvider";
import MessageNotifications from "./MessageNotifications";
import { Link } from "react-router";

export type NavigationBarProps = {
  scrollPosition?: number;
  minimal: boolean;
  clickableLogo: boolean;
  hideMainNavigation?: boolean;
};

const NavigationBar = ({ scrollPosition = 0, minimal, clickableLogo, hideMainNavigation = false }: NavigationBarProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openMenu, setOpenMenu] = useState("");
  const scrolledStyle = scrollPosition > 1;
  const { isLoggedIn } = useCustomer();

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, name: string) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(name);
  };

  const handleClose = () => {
    setOpenMenu("");
    setAnchorEl(null);
  };
  return (
    <>
      <MuiLink component={Link} to={"#main-content"} sx={navigationBarStyle.navSkipLink}>
        Zum Hauptinhalt springen
      </MuiLink>
      <AppBar position={"fixed"} color={scrolledStyle ? "inherit" : "transparent"} elevation={scrolledStyle ? 4 : 0}>
        <Toolbar sx={navigationBarStyle.toolBar}>
          <Box component={"nav"} aria-label={"Hauptnavigation"} sx={{ width: "100%" }}>
            <Grid
              container
              columnSpacing={5}
              sx={[
                {
                  alignItems: "center",
                },
                navigationBarStyle.navigationContainer,
              ]}
            >
              <Grid>
                <Logo target={isLoggedIn ? "/servicewelt" : "/"} isClickable={clickableLogo} />
              </Grid>
              {!minimal && (
                <>
                  <Grid>
                    {!hideMainNavigation && (
                      <>
                        <Button
                          color="buttonText"
                          endIcon={openMenu === "areas" ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
                          onClick={(event) => handleOpenMenu(event, "areas")}
                          aria-expanded={openMenu === "areas"}
                          aria-controls={"areas-menu"}
                          aria-haspopup={"true"}
                          sx={navigationBarStyle.navMenuButton}
                        >
                          Rechtsbereiche
                        </Button>
                        <Button
                          color="buttonText"
                          endIcon={openMenu === "guidebooks" ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
                          onClick={(event) => handleOpenMenu(event, "guidebooks")}
                          aria-expanded={openMenu === "guidebooks"}
                          aria-controls={"guidebooks-menu"}
                          aria-haspopup={"true"}
                          sx={navigationBarStyle.navMenuButton}
                        >
                          Ratgeber
                        </Button>
                        <Button
                          color="buttonText"
                          endIcon={openMenu === "aboutUs" ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
                          onClick={(event) => handleOpenMenu(event, "aboutUs")}
                          aria-expanded={openMenu === "aboutUs"}
                          aria-controls={"aboutUs-menu"}
                          aria-haspopup={"true"}
                          sx={navigationBarStyle.navMenuButton}
                        >
                          Über Legalbird
                        </Button>
                      </>
                    )}
                    <Menu
                      id={"areas-menu"}
                      open={openMenu === "areas"}
                      onClose={handleClose}
                      anchorEl={anchorEl}
                      slotProps={{
                        paper: {
                          style: { minWidth: 150 },
                        },
                      }}
                    >
                      {_map(navigationLinks.areas, (link, index) => (
                        <MenuItem component={Link} to={link.to} key={index}>
                          {link.label}
                        </MenuItem>
                      ))}
                    </Menu>
                    <Menu
                      id={"guidebooks-menu"}
                      open={openMenu === "guidebooks"}
                      onClose={handleClose}
                      anchorEl={anchorEl}
                      slotProps={{
                        paper: {
                          style: { minWidth: 150 },
                        },
                      }}
                    >
                      {_map(navigationLinks.guidebooks, (link, index) => (
                        <MenuItem component={Link} to={link.to} key={index}>
                          {link.label}
                        </MenuItem>
                      ))}
                    </Menu>
                    <Menu
                      id={"aboutUs-menu"}
                      open={openMenu === "aboutUs"}
                      onClose={handleClose}
                      anchorEl={anchorEl}
                      slotProps={{
                        paper: {
                          style: { minWidth: 150 },
                        },
                      }}
                    >
                      {_map(navigationLinks.aboutUs, (link, index) => (
                        <MenuItem component={Link} to={link.to} key={index}>
                          {link.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Grid>
                  <Grid
                    sx={{
                      flexGrow: 1,
                    }}
                  >
                    <Stack
                      direction={"row"}
                      spacing={2}
                      sx={{
                        justifyContent: "flex-end",
                        alignItems: "center",
                      }}
                    >
                      {isLoggedIn ? <MessageNotifications /> : null}
                      {isLoggedIn ? <LoggedInMenu /> : <LoginIcon />}
                    </Stack>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default withScroll(NavigationBar);
